import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { IntegrationsApi } from 'entities/Integrations/api/integrations'
import { IntegrationKey } from 'entities/Integrations'
import { IntegrationCrmUser } from 'entities/Integrations/model/IntegrationCrmUser'

const MIN_SEARCH_LENGTH = 3

export class ClientInviteFieldStore {
  private _inputValue = ''
  private _displayValue = ''
  private _isDropdownOpen = false
  private _hubSpotUsers: IntegrationCrmUser[] = []
  private _loading = false
  private _emailError = ''

  constructor() {
    makeAutoObservable(this)
  }

  get inputValue() {
    return this._inputValue
  }

  get displayValue() {
    return this._displayValue
  }

  get isDropdownOpen() {
    return this._isDropdownOpen
  }

  get loading() {
    return this._loading
  }

  get emailError() {
    return this._emailError
  }

  get canSearch() {
    return this._inputValue.trim().length >= MIN_SEARCH_LENGTH
  }

  get selectedEmail() {
    return this._displayValue.trim()
  }

  get filteredUsers(): IntegrationCrmUser[] {
    if (!this.canSearch) return []
    const searchLower = this._inputValue.trim().toLowerCase()
    return this._hubSpotUsers.filter((user) => {
      if (!user.email) return false
      if (!searchLower) return true

      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase()
      const email = user.email.toLowerCase()

      return fullName.includes(searchLower) || email.includes(searchLower)
    })
  }

  setEmailError = (error: string) => {
    this._emailError = error
  }

  loadHubSpotUsers = async () => {
    if (this._hubSpotUsers.length > 0 || this._loading) return

    this._loading = true
    try {
      const {
        data: { userIntegrations = [] },
      } = await IntegrationsApi.getIntegrationsList()

      const hubspot = userIntegrations.find(
        (ui) =>
          ui.integration?.key === IntegrationKey.hubspot && ui.is_active && ui.crm_users?.length
      )

      runInAction(() => {
        this._hubSpotUsers = (hubspot?.crm_users ?? []).map((u) => new IntegrationCrmUser(u))
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  handleInput = (value: string) => {
    this._emailError = ''
    this._inputValue = value
  }

  selectUser = (user: IntegrationCrmUser) => {
    const value = user.email.trim() || user.name
    this._displayValue = value
    this._inputValue = value
    this._emailError = ''
    this._isDropdownOpen = false
  }

  toggleDropdown = (open: boolean) => {
    if (open) {
      this._inputValue = this._displayValue
      this._isDropdownOpen = true
    } else {
      this._displayValue = this._inputValue
      this._isDropdownOpen = false
    }
  }

  reset = () => {
    this._inputValue = ''
    this._displayValue = ''
    this._emailError = ''
    this._hubSpotUsers = []
  }
}
